import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';

type FirebaseFunction = (...args: never[]) => unknown;

/**
 * Re-enters Angular's injection context for Firebase APIs called after awaits,
 * effects, timers, or other asynchronous boundaries.
 */
export function bindFirebaseInjectionContext<T>(api: T): T {
  const environmentInjector = inject(EnvironmentInjector);
  const wrap = (fn: FirebaseFunction): FirebaseFunction =>
    ((...args: readonly unknown[]) =>
      runInInjectionContext(environmentInjector, () =>
        Reflect.apply(fn, undefined, args),
      )) as FirebaseFunction;

  if (typeof api === 'function') {
    return (isClassConstructor(api) ? api : wrap(api as FirebaseFunction)) as T;
  }

  return new Proxy(api as object, {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver);
      if (typeof value !== 'function' || isClassConstructor(value)) return value;
      return wrap(value as FirebaseFunction);
    },
  }) as T;
}

export function injectFirebaseContextRunner(): <T>(action: () => T) => T {
  const environmentInjector = inject(EnvironmentInjector);
  return (action) => runInInjectionContext(environmentInjector, action);
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function isClassConstructor(value: Function): boolean {
  const prototype = Object.getOwnPropertyDescriptor(value, 'prototype');
  return prototype !== undefined && !prototype.writable;
}
